import { useEffect, useRef, useState } from "react";
import { LuCircleAlert, LuLoaderCircle } from "react-icons/lu";
import samsungBezel from "../../assets/device_bezels/samsung_bezel.png";
import samsungTabletBezel from "../../assets/device_bezels/samsung_tablet_bezel.png";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useIsNewProject } from "../../core_stores/useProjectStore";
import { trackEvent, trackPageview } from "../../utils/analyticsUtils";
import { useChatMessagesActions } from "../../view_stores/chat_messages/useChatMessagesStore";
import {
  PreviewMode,
  PreviewUrlStatus,
  useProjectPreviewActions,
  useProjectPreviewIsPaused,
  useProjectPreviewMode,
  useProjectPreviewScale,
  useProjectPreviewStatus,
  useProjectPreviewUrl,
  useProjectPreviewVersion,
} from "../../view_stores/useProjectPreviewStore";
import { ProjectPreviewScaleControls } from "./components/ProjectPreviewScaleControls";
import { ProjectPreviewScreenshotControls } from "./components/ProjectPreviewScreenshotControls";

export function ProjectPreview() {
  const previewUrl = useProjectPreviewUrl();
  const previewStatus = useProjectPreviewStatus();
  const previewMode = useProjectPreviewMode();
  const isCompactView = useIsCompactView();

  const { setPreviewMode } = useProjectPreviewActions();

  // DEBUG: Track when previewUrl changes in ProjectPreview component
  console.log(
    `[DEBUG ${new Date().toISOString()}] ProjectPreview render - previewUrl:`,
    previewUrl
  );

  useEffect(() => {
    trackPageview("Preview");
  }, []);

  useEffect(() => {
    if (isCompactView) {
      setPreviewMode(PreviewMode.MobilePortrait);
    }
  }, [isCompactView]);

  if (previewStatus === PreviewUrlStatus.NotAvailable) {
    return <div className="flex-1 w-full h-full" />;
  }

  return isCompactView ? (
    <Preview className="w-full h-full border-0" />
  ) : (
    <div className="w-full h-full">
      <MobilePreview />
      {previewMode !== PreviewMode.Desktop && <ProjectPreviewScaleControls />}
      {previewMode !== PreviewMode.Desktop && (
        <ProjectPreviewScreenshotControls className="fixed bottom-20 right-6 z-40 flex flex-col items-end justify-end" />
      )}
    </div>
  );
}

function Preview({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  const previewUrlVersion = useProjectPreviewVersion();
  const previewUrl = useProjectPreviewUrl();
  const previewStatus = useProjectPreviewStatus();
  const isNewProject = useIsNewProject();
  const isPaused = useProjectPreviewIsPaused();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { setIframeRef } = useProjectPreviewActions();
  const { addPreviewError } = useChatMessagesActions();

  const [isLoaded, setIsLoaded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    // Reset loading state when URL or refresh key changes
    setIsLoaded(false);
  }, [previewUrlVersion]);

  // Handle overlay animation
  useEffect(() => {
    const shouldShow = previewStatus !== PreviewUrlStatus.Ready || !isLoaded;

    if (shouldShow && !showOverlay) {
      // Fade in: first mount with opacity-0, then transition to opacity-100
      setShowOverlay(true);
      setIsAnimatingOut(true); // Start with opacity-0

      // Trigger fade in on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingOut(false); // Transition to opacity-100
        });
      });
    } else if (!shouldShow && showOverlay) {
      // Fade out
      setIsAnimatingOut(true);
      const timeout = setTimeout(() => {
        setShowOverlay(false);
        setIsAnimatingOut(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timeout);
    }
  }, [previewStatus, isLoaded, showOverlay]);

  useEffect(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      let errorBuffer: string[] = [];
      let errorTimeout: NodeJS.Timeout | null = null;
      const ERROR_DEBOUNCE_MS = 100;

      const flushErrors = () => {
        if (errorBuffer.length > 0) {
          const mergedError = errorBuffer.join("\n");
          console.error("Flutter Error:", mergedError);
          addPreviewError(mergedError);
          errorBuffer = [];
        }
        if (errorTimeout) {
          clearTimeout(errorTimeout);
          errorTimeout = null;
        }
      };

      (iframeWindow as any).console.log = function (...args: any[]) {
        const errorMessage = args.join(" ");
        errorBuffer.push(errorMessage);

        if (errorTimeout) {
          clearTimeout(errorTimeout);
        }

        errorTimeout = setTimeout(flushErrors, ERROR_DEBOUNCE_MS);
      };
    }
  }, [iframeRef.current?.contentWindow]);

  useEffect(() => {
    if (isLoaded) {
      if (previewStatus == PreviewUrlStatus.Ready && isNewProject) {
        trackEvent("preview_shown");
      }

      const isFirstPreviewShown = localStorage.getItem("isFirstPreviewShown");
      if (!isFirstPreviewShown) {
        localStorage.setItem("isFirstPreviewShown", "true");
        trackEvent("preview_shown_first");
      }
    }
  }, [isLoaded]);

  // Show paused state - unmount iframe to save CPU
  if (isPaused) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-dark-surface-secondary`}
        style={style}
      >
        <span className="text-dark-text-secondary text-lg font-medium">
          PAUSED
        </span>
      </div>
    );
  }

  const overlayTextColor =
    previewStatus === PreviewUrlStatus.Error ? "text-red-500" : "text-white";

  return (
    <>
      {showOverlay && (
        <div
          className={`absolute inset-0 
            bg-gray-900/85
            flex flex-col items-center justify-center
            gap-3 z-10 pointer-events-auto
            transition-opacity duration-300 ease-in-out
            ${isAnimatingOut ? "opacity-0" : "opacity-100"}`}
        >
          {previewStatus === PreviewUrlStatus.Error ? (
            <LuCircleAlert className={`w-10 h-10 ${overlayTextColor}`} />
          ) : (
            <LuLoaderCircle
              className={`w-10 h-10 ${overlayTextColor} animate-spin`}
            />
          )}
          <span className={`text-base font-medium ${overlayTextColor}`}>
            {previewStatus === PreviewUrlStatus.Updating
              ? "Updating preview..."
              : previewStatus === PreviewUrlStatus.Error
              ? "Preview update failed"
              : "Loading preview..."}
          </span>
        </div>
      )}
      <iframe
        key={previewUrlVersion ?? ""}
        ref={iframeRef}
        src={previewUrl ?? ""}
        className={`${className} ${isLoaded ? "block" : "hidden"}`}
        style={style}
        title="Mobile Preview"
        onLoad={() => {
          setIsLoaded(true);
          setIframeRef(iframeRef);
          const parentFocus = document.activeElement;
          const interval = setInterval(() => {
            const newFocus = document.activeElement;
            if (newFocus?.tagName === "IFRAME" && parentFocus) {
              (parentFocus as HTMLElement).focus();
              clearInterval(interval);
            }
          }, 4);
        }}
      />
    </>
  );
}

function MobilePreview() {
  const previewMode = useProjectPreviewMode();
  const deviceScale = useProjectPreviewScale();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [baseScale, setBaseScale] = useState(1);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const prevModeRef = useRef(previewMode);
  const isResizingRef = useRef(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });

        // Set resizing flag and clear previous timeout
        isResizingRef.current = true;
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        // Clear resizing flag after resize ends
        resizeTimeoutRef.current = setTimeout(() => {
          isResizingRef.current = false;
        }, 150);
      }
    });

    resizeObserver.observe(containerRef.current);

    // Enable animations after initial render
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, 100);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timer);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Handle preview mode changes
  useEffect(() => {
    if (prevModeRef.current !== previewMode) {
      setShouldAnimate(false);
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, 100);
      prevModeRef.current = previewMode;
      return () => clearTimeout(timer);
    }
  }, [previewMode]);

  let deviceBezelSrc: string | undefined;
  let bezelScale: number | undefined;
  let bezelRotation: number | undefined;
  let globalContainerStyle: React.CSSProperties | undefined;
  let iFrameContainerStyle: React.CSSProperties | undefined;
  let iFrameStyle: React.CSSProperties | undefined;

  if (previewMode === PreviewMode.Desktop) {
    globalContainerStyle =
      iFrameContainerStyle =
      iFrameStyle =
        {
          width: "100%",
          height: "100%",
        };
  } else {
    const {
      bezelSrc,
      physicalWidth,
      physicalHeight,
      logicalWidth,
      logicalHeight,
      iFrameContainerPercentageLeft,
      iFrameContainerPercentageTop,
      iFrameContainerPercentageWidth,
      iFrameContainerPercentageHeight,
      screenRadius,
      scale,
      rotation,
    } = getBezelProps(previewMode);

    deviceBezelSrc = bezelSrc;
    bezelScale = scale;
    bezelRotation = rotation;

    const contentScale = Math.min(
      physicalWidth / logicalWidth,
      physicalHeight / logicalHeight
    );

    let adjustedScale = deviceScale;
    if (containerSize.width > 0 && containerSize.height > 0) {
      const widthScale = containerSize.width / physicalWidth;
      const heightScale = containerSize.height / physicalHeight;
      adjustedScale = Math.min(widthScale, heightScale) * deviceScale;

      if (adjustedScale === 1 && adjustedScale !== baseScale) {
        setBaseScale(adjustedScale);
      }
    }

    const adjustedScreenRadius = `${screenRadius}% / ${
      screenRadius * (physicalWidth / physicalHeight)
    }%`;

    const shouldApplyAnimation = shouldAnimate && !isResizingRef.current;

    globalContainerStyle = {
      width: physicalWidth * adjustedScale,
      height: physicalHeight * adjustedScale,
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      transition: shouldApplyAnimation
        ? "width 0.3s ease-in-out, height 0.3s ease-in-out"
        : "none",
    };
    iFrameContainerStyle = {
      left: `${iFrameContainerPercentageLeft}%`,
      top: `${iFrameContainerPercentageTop}%`,
      width: `${iFrameContainerPercentageWidth}%`,
      height: `${iFrameContainerPercentageHeight}%`,
      borderRadius: adjustedScreenRadius,
    };
    iFrameStyle = {
      width: `${100 / (contentScale * adjustedScale)}%`,
      height: `${100 / (contentScale * adjustedScale)}%`,
      transform: `scale(${contentScale * adjustedScale})`,
      transformOrigin: "0 0",
      border: "none",
      borderRadius: adjustedScreenRadius,
      transition: shouldApplyAnimation
        ? "transform 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out"
        : "none",
    };
  }

  // Hide container until we have valid dimensions to prevent flash of unstyled iframe
  const hasValidSize = containerSize.width > 0 && containerSize.height > 0;

  return (
    <div
      className={`${
        previewMode === PreviewMode.Desktop ? "" : "p-6"
      } w-full h-full relative`}
      ref={containerRef}
      style={{ visibility: hasValidSize ? "visible" : "hidden" }}
    >
      <div className="absolute" style={globalContainerStyle}>
        <div
          className="absolute bg-black overflow-hidden"
          style={iFrameContainerStyle}
        >
          <Preview style={iFrameStyle} />
        </div>
        {previewMode !== PreviewMode.Desktop && (
          <img
            src={deviceBezelSrc!}
            className="absolute w-full h-full pointer-events-none select-none object-contain"
            style={{
              transform: `scale(${bezelScale}) rotate(${bezelRotation}deg)`,
            }}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
}

interface BezelProps {
  bezelSrc: string;
  physicalWidth: number;
  physicalHeight: number;
  logicalWidth: number;
  logicalHeight: number;
  iFrameContainerPercentageLeft: number;
  iFrameContainerPercentageTop: number;
  iFrameContainerPercentageWidth: number;
  iFrameContainerPercentageHeight: number;
  screenRadius: number;
  scale: number;
  rotation: number;
}

function getBezelProps(previewMode: PreviewMode): BezelProps {
  switch (previewMode) {
    case PreviewMode.MobilePortrait:
      return {
        bezelSrc: samsungBezel,
        physicalWidth: 1440,
        physicalHeight: 3120,
        logicalWidth: 480,
        logicalHeight: 1040,
        iFrameContainerPercentageLeft: 5.5,
        iFrameContainerPercentageTop: 6.3,
        iFrameContainerPercentageWidth: 89,
        iFrameContainerPercentageHeight: 87.4,
        screenRadius: 5,
        scale: 1.1,
        rotation: 0,
      };
    case PreviewMode.MobileLandscape:
      return {
        bezelSrc: samsungBezel,
        physicalWidth: 3120,
        physicalHeight: 1440,
        logicalWidth: 1040,
        logicalHeight: 480,
        iFrameContainerPercentageLeft: 6.1,
        iFrameContainerPercentageTop: 5.3,
        iFrameContainerPercentageWidth: 87.8,
        iFrameContainerPercentageHeight: 89.4,
        screenRadius: 2,
        scale: 2.1,
        rotation: -90,
      };
    case PreviewMode.Tablet:
      return {
        bezelSrc: samsungTabletBezel,
        physicalWidth: 2960,
        physicalHeight: 1848,
        logicalWidth: 986,
        logicalHeight: 616,
        iFrameContainerPercentageLeft: 7.5,
        iFrameContainerPercentageTop: 6.7,
        iFrameContainerPercentageWidth: 85,
        iFrameContainerPercentageHeight: 86.5,
        screenRadius: 2.5,
        scale: 1.5,
        rotation: -90,
      };
    default:
      throw new Error(`Unknown preview mode: ${previewMode}`);
  }
}
